import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { useMemo, useState } from 'react'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import EmptyState from '../../shared/components/EmptyState'
import FilterSelect from '../../shared/components/FilterSelect'
import StatCard from '../../shared/components/StatCard'
import SharedTable from '../../shared/components/Table'
import { useThemeMode } from '../../shared/hooks'
import { abbreviate, dayjsFormat, formatCount, formatCurrency } from '../../shared/utilities/util'
import { AiMonthlyUsage, AiUsageMetrics, useGetCompanyAiUsageQuery } from '../../store/api/adminApi'
import dayjs from 'dayjs'

type Metric = 'cost' | 'tokens'

const emptyMetrics = (): AiUsageMetrics => ({
  inputTokens: 0,
  outputTokens: 0,
  transcriptionSeconds: 0,
  llmCostCents: 0,
  transcriptionCostCents: 0,
  requests: 0,
  totalCostCents: 0,
})

// Accumulates `b` into `a` in place.
const addMetrics = (a: AiUsageMetrics, b?: AiUsageMetrics) => {
  if (!b) return a
  a.inputTokens += b.inputTokens ?? 0
  a.outputTokens += b.outputTokens ?? 0
  a.transcriptionSeconds += b.transcriptionSeconds ?? 0
  a.llmCostCents += b.llmCostCents ?? 0
  a.transcriptionCostCents += b.transcriptionCostCents ?? 0
  a.requests += b.requests ?? 0
  a.totalCostCents += b.totalCostCents ?? 0
  return a
}

type ModuleMeta = { key: string; color: string; darkColor: string }

// Canonical module ORDER + COLORS (light / dark), matching the app's recharts palette.
// This is NOT the list of rendered modules — that comes from the API response (see the
// `modules` memo below), which is self-describing (`byModule[].module` + `.label`) and
// already includes zero-usage modules. Keys here that the backend doesn't report are
// simply not rendered; backend keys missing here still render, colored from
// FALLBACK_COLORS. So a new AI bucket needs no change to this file to show up.
const MODULE_META: ModuleMeta[] = [
  { key: 'callAnalysis', color: '#2563eb', darkColor: '#60a5fa' },
  { key: 'rideAlong', color: '#7c3aed', darkColor: '#a78bfa' },
  { key: 'aiAgent', color: '#0d9488', darkColor: '#2dd4bf' },
  { key: 'materialSheetScan', color: '#db2777', darkColor: '#f472b6' },
  { key: 'teamReviewNarrative', color: '#d97706', darkColor: '#fbbf24' },
]

// Deterministic colors for modules the API reports that aren't in MODULE_META yet,
// cycled by position. Ordered most-distinguishable-first (lime, zinc, cyan, green:
// min ΔE 27+ against every canonical color in BOTH themes) so an unrecognized bucket
// is still legible in the stack. Red is deliberately unused — it reads as the error state.
const FALLBACK_COLORS: Omit<ModuleMeta, 'key'>[] = [
  { color: '#65a30d', darkColor: '#a3e635' },
  { color: '#52525b', darkColor: '#a1a1aa' },
  { color: '#0891b2', darkColor: '#22d3ee' },
  { color: '#16a34a', darkColor: '#4ade80' },
]

const toMinutes = (seconds?: number) => Math.round((seconds ?? 0) / 60)

const AiUsageTooltip = ({ active, payload, label, metric }: any) => {
  if (!active || !payload?.length) return null
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '10px',
        px: 1.5,
        py: 1,
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        minWidth: 160,
      }}
    >
      <Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>{label}</Typography>
      {payload.map((entry: any, i: number) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 0.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: entry.color }} />
            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>{entry.name}</Typography>
          </Box>
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'text.primary' }}>
            {metric === 'cost' ? formatCurrency(Math.round((entry.value ?? 0) * 100)) : formatCount(entry.value)}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

const AiUsageLegend = ({ payload }: any) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      gap: 2,
      flexWrap: 'wrap',
      mt: 2,
      pt: 1.5,
      borderTop: '1px solid',
      borderColor: 'divider',
    }}
  >
    {payload?.map((entry: any, i: number) => (
      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: entry.color }} />
        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'text.primary' }}>{entry.value}</Typography>
      </Box>
    ))}
  </Box>
)

const DataCell = ({ children, bold }: { children: React.ReactNode; bold?: boolean }) => (
  <Box component="span" sx={{ display: 'block', textAlign: 'center', fontWeight: bold ? 700 : 400 }}>
    {children}
  </Box>
)

const tableColumns = [
  { id: 'module', label: 'Module', width: 'auto' },
  { id: 'requests', label: 'Requests', width: 'auto', align: 'center' },
  { id: 'inputTokens', label: 'Input tokens', width: 'auto', align: 'center' },
  { id: 'outputTokens', label: 'Output tokens', width: 'auto', align: 'center' },
  { id: 'transcriptionMin', label: 'Transcription min', width: 'auto', align: 'center' },
  { id: 'llmCost', label: 'LLM cost', width: 'auto', align: 'center' },
  { id: 'transcriptionCost', label: 'Transcription cost', width: 'auto', align: 'center' },
  { id: 'totalCost', label: 'Total cost', width: 'auto', align: 'center' },
]

const AiUsage = ({ id }: { id?: string }) => {
  const { isDark } = useThemeMode()
  const [metric, setMetric] = useState<Metric>('cost')

  const monthOptions = useMemo(() => {
    const opts: { id: string; name: string }[] = []
    for (let i = 0; i < 24; i++) {
      const d = dayjs().subtract(i, 'month')
      opts.push({ id: d.format('YYYY-MM'), name: d.format('MMM YYYY') })
    }
    return opts
  }, [])

  const [startMonth, setStartMonth] = useState(() => dayjs().subtract(5, 'month').format('YYYY-MM'))
  const [endMonth, setEndMonth] = useState(() => dayjs().format('YYYY-MM'))

  const {
    data: usage,
    isLoading,
    isFetching,
    isError,
  } = useGetCompanyAiUsageQuery({ companyId: id as string, startMonth, endMonth }, { skip: !id })

  const loading = isLoading || isFetching
  const total = usage?.total
  // Memoized: a bare `?? []` literal gets a new identity every render, which would
  // invalidate every derived memo below on each render.
  const byModule = useMemo(() => usage?.byModule ?? [], [usage])
  const monthly = useMemo(() => usage?.monthly ?? [], [usage])
  const hasUsage = (total?.requests ?? 0) > 0

  const moduleLabel = useMemo(() => {
    const map: Record<string, string> = {}
    byModule.forEach((m) => {
      map[m.module] = m.label
    })
    return map
  }, [byModule])

  // The modules actually rendered (chart series, legend, tooltip, table rows).
  // Derived from the API response so nothing is ever silently dropped: every module
  // the endpoint reports gets a row/series — MODULE_META only supplies the canonical
  // ordering and colors. Keys the API reports but MODULE_META doesn't know get a
  // deterministic fallback color and are appended in API order.
  const modules = useMemo<ModuleMeta[]>(() => {
    const apiKeys: string[] = []
    const collect = (key: string) => {
      if (key && !apiKeys.includes(key)) apiKeys.push(key)
    }
    // `byModule` is the canonical, backend-ordered list (zero-usage modules included);
    // `monthly` is scanned too so a bucket present only in the trend series still renders.
    byModule.forEach((m) => collect(m.module))
    monthly.forEach((m: AiMonthlyUsage) => Object.keys(m.byModule ?? {}).forEach(collect))

    const known = new Set(MODULE_META.map((m) => m.key))
    const unknown = apiKeys.filter((key) => !known.has(key))

    return [
      ...MODULE_META.filter((m) => apiKeys.includes(m.key)),
      ...unknown.map((key, i) => ({ key, ...FALLBACK_COLORS[i % FALLBACK_COLORS.length] })),
    ]
  }, [byModule, monthly])

  // Range-scoped aggregates derived from the range-filtered `monthly[]` so the
  // cards + table track the From/To filter (the endpoint's `total`/`byModule`
  // are lifetime accumulators and never respond to the range).
  const rangeByModule = useMemo(() => {
    const acc: Record<string, AiUsageMetrics> = {}
    modules.forEach(({ key }) => {
      acc[key] = emptyMetrics()
    })
    monthly.forEach((m: AiMonthlyUsage) => {
      modules.forEach(({ key }) => {
        addMetrics(acc[key], m.byModule?.[key])
      })
    })
    return acc
  }, [monthly, modules])

  const rangeTotal = useMemo(() => {
    const acc = emptyMetrics()
    monthly.forEach((m: AiMonthlyUsage) => addMetrics(acc, m.total))
    return acc
  }, [monthly])

  const chartData = useMemo(
    () =>
      monthly.map((m: AiMonthlyUsage) => {
        const row: Record<string, any> = { month: dayjsFormat(`${m.month}-01`, 'MMM YY') }
        modules.forEach(({ key }) => {
          const bm = m.byModule?.[key]
          row[key] =
            metric === 'cost' ? (bm?.totalCostCents ?? 0) / 100 : (bm?.inputTokens ?? 0) + (bm?.outputTokens ?? 0)
        })
        return row
      }),
    [monthly, metric, modules]
  )

  const tableData = useMemo(() => {
    const rows = modules.map(({ key, color, darkColor }) => {
      const m = rangeByModule[key]
      // Data-driven "not applicable" dash, not key-driven: LLM-only modules (aiAgent,
      // materialSheetScan, teamReviewNarrative, …) never report transcription, so a
      // hardcoded key check would print a misleading 0 / $0.00 for every new one.
      const hasTranscription = (m?.transcriptionSeconds ?? 0) > 0 || (m?.transcriptionCostCents ?? 0) > 0
      return {
        module: (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: isDark ? darkColor : color, flexShrink: 0 }}
            />
            <Typography component="span" sx={{ fontSize: 'inherit', color: 'text.primary' }}>
              {moduleLabel[key] ?? key}
            </Typography>
          </Box>
        ),
        requests: <DataCell>{(m?.requests ?? 0).toLocaleString('en-US')}</DataCell>,
        inputTokens: <DataCell>{formatCount(m?.inputTokens)}</DataCell>,
        outputTokens: <DataCell>{formatCount(m?.outputTokens)}</DataCell>,
        transcriptionMin: (
          <DataCell>
            {hasTranscription ? (
              toMinutes(m?.transcriptionSeconds).toLocaleString('en-US')
            ) : (
              <Box component="span" sx={{ color: 'text.secondary' }}>
                —
              </Box>
            )}
          </DataCell>
        ),
        llmCost: <DataCell>{formatCurrency(m?.llmCostCents)}</DataCell>,
        transcriptionCost: (
          <DataCell>
            {hasTranscription ? (
              formatCurrency(m?.transcriptionCostCents)
            ) : (
              <Box component="span" sx={{ color: 'text.secondary' }}>
                —
              </Box>
            )}
          </DataCell>
        ),
        totalCost: <DataCell bold>{formatCurrency(m?.totalCostCents)}</DataCell>,
      }
    })

    rows.push({
      module: (
        <Typography component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Total
        </Typography>
      ),
      requests: <DataCell bold>{rangeTotal.requests.toLocaleString('en-US')}</DataCell>,
      inputTokens: <DataCell bold>{formatCount(rangeTotal.inputTokens)}</DataCell>,
      outputTokens: <DataCell bold>{formatCount(rangeTotal.outputTokens)}</DataCell>,
      transcriptionMin: <DataCell bold>{toMinutes(rangeTotal.transcriptionSeconds).toLocaleString('en-US')}</DataCell>,
      llmCost: <DataCell bold>{formatCurrency(rangeTotal.llmCostCents)}</DataCell>,
      transcriptionCost: <DataCell bold>{formatCurrency(rangeTotal.transcriptionCostCents)}</DataCell>,
      totalCost: <DataCell bold>{formatCurrency(rangeTotal.totalCostCents)}</DataCell>,
    })

    return rows
  }, [modules, rangeByModule, rangeTotal, moduleLabel, isDark])

  const panelBorder = `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <FilterSelect
          label="From"
          value={startMonth}
          options={monthOptions}
          onChange={setStartMonth}
          includeAll={false}
        />
        <FilterSelect label="To" value={endMonth} options={monthOptions} onChange={setEndMonth} includeAll={false} />
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={metric}
          onChange={(_, v) => v && setMetric(v)}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontSize: '12.5px',
              fontWeight: 600,
              px: 2,
              py: 0.75,
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
              color: 'text.secondary',
              '&.Mui-selected': {
                bgcolor: isDark ? 'rgba(37,99,235,0.2)' : 'rgba(37,99,235,0.08)',
                color: isDark ? '#60a5fa' : '#2563eb',
                '&:hover': { bgcolor: isDark ? 'rgba(37,99,235,0.25)' : 'rgba(37,99,235,0.12)' },
              },
            },
          }}
        >
          <ToggleButton value="cost">Cost</ToggleButton>
          <ToggleButton value="tokens">Tokens</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {isError ? (
        <Box
          sx={{
            p: 3,
            border: `1px solid ${isDark ? 'rgba(239,68,68,0.2)' : '#fecaca'}`,
            borderRadius: '12px',
            bgcolor: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2',
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'error.main' }}>
            Failed to load AI usage. Please try again.
          </Typography>
        </Box>
      ) : loading ? (
        <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary', fontSize: '14px' }}>Loading AI usage…</Box>
      ) : !hasUsage ? (
        <Box sx={{ border: panelBorder, borderRadius: '12px', bgcolor: 'background.paper' }}>
          <EmptyState
            icon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />}
            title="No AI usage yet"
            subtitle="Usage and cost will appear here once this company's first AI action runs."
          />
        </Box>
      ) : (
        <>
          {/* Summary cards */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
              gap: 2,
            }}
          >
            <StatCard label="AI Cost (est.) · Selected range" value={formatCurrency(rangeTotal.totalCostCents)} />
            <StatCard label="AI Cost (est.) · Lifetime" value={formatCurrency(total?.totalCostCents)} />
            <StatCard
              label="LLM Tokens (in / out) · Range"
              value={`${abbreviate(rangeTotal.inputTokens)} / ${abbreviate(rangeTotal.outputTokens)}`}
            />
            <StatCard label="AI Requests · Range" value={rangeTotal.requests.toLocaleString('en-US')} />
          </Box>

          {/* Chart */}
          <Box sx={{ p: 2.5, border: panelBorder, borderRadius: '12px', bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1 }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                Monthly AI {metric === 'cost' ? 'Cost' : 'Tokens'} by Module
              </Typography>
              <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                {metric === 'cost' ? 'estimated vendor cost (USD)' : 'input + output tokens'}
              </Typography>
            </Box>
            {chartData.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary', fontSize: '13px' }}>
                No data in the selected range
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fill: isDark ? '#64748b' : '#94a3b8' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: isDark ? '#64748b' : '#94a3b8' }}
                    axisLine={false}
                    tickLine={false}
                    width={48}
                    tickFormatter={(v: number) => (metric === 'cost' ? `$${abbreviate(v)}` : abbreviate(v))}
                  />
                  <Tooltip
                    cursor={{ fill: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }}
                    content={<AiUsageTooltip metric={metric} />}
                  />
                  <Legend content={<AiUsageLegend />} />
                  {modules.map(({ key, color, darkColor }, i) => (
                    <Bar
                      key={key}
                      dataKey={key}
                      name={moduleLabel[key] ?? key}
                      stackId="ai"
                      fill={isDark ? darkColor : color}
                      // Round only the series on TOP of the stack — recharts stacks in
                      // declaration order, so that is always the last one rendered.
                      radius={i === modules.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                      maxBarSize={48}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>

          {/* Per-module breakdown */}
          <Box>
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                mb: 1.5,
              }}
            >
              Breakdown by Module
            </Typography>
            <SharedTable
              columns={tableColumns}
              data={tableData}
              page={0}
              rowsPerPage={tableData.length}
              totalRows={tableData.length}
              onPageChange={() => {}}
              onRowsPerPageChange={() => {}}
              tableBodyLoader={false}
              onSearch={() => {}}
              searchFilter={undefined}
              searchFilterVisible={false}
              selectableRows={false}
              onRowClick={() => {}}
              getRowHref={undefined}
              showDeletedToggle={false}
              switchProps={undefined}
              showPagination={false}
            />
          </Box>
        </>
      )}
    </Box>
  )
}

export default AiUsage
